
import Alert from "@/components/Alert";
import SiteHeader from "@/components/SiteHeader";
import firebaseApp from "@/firebaseApp";
import { authStateHandler, backendExceptionHandler, languageHandler, routeLanguageHandler } from "@/mixins";
import AuthService from '@/service/AuthService';
import PortfolioService from "@/service/PortfolioService";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { THEME_MODE_OPTIONS } from '../constants/theme.constants';
import GlobalSnackBar from '@/components/shared/GlobalSnackBar';
import HelpDialogsContainer from '@/components/shared/HelpDialogsContainer';
import FinAgentService from '@/service/FinAgentService';
import subscriptionConstants from '@/constants/subscription.constants';

const { subscribeProductKeys } = subscriptionConstants;
const auth = getAuth(firebaseApp);

const PING_INTERVAL_MS = 5000  // milliseconds

export default {
    name: 'App',
    components: { Alert, SiteHeader, GlobalSnackBar, HelpDialogsContainer },
    mixins: [backendExceptionHandler, authStateHandler, languageHandler, routeLanguageHandler],
    data() {
        return {
            errorMessage: null,
            portfolios: [],
            overlay: false,
            networkError: false,
            componentKey: true,
            authInitialized: false || process.server,
            settingThemeMode: THEME_MODE_OPTIONS.light
        }
    },
    computed: {
        isLoggedIn() {
            return this.$store.state.account.user
        },
        themeMode() {
            return this.$store.state.theme.themeMode
        },
        isSubscribedFinAgent() {
            return this.$store.state.account.subscribedProductIds?.includes(subscribeProductKeys.finAgent)
        },
    },
    watch: {
        isLoggedIn(user) {
            if (!user) {
                return
            }
            if (!this.isSubscribedFinAgent) {
                this.fetchQuestionUsage();
            }
            this.fetchAccountCurrency()
        },
    },
    created() {
        this.finAgentService = new FinAgentService(this.$axios)
        this.portfolioService = new PortfolioService(this.$axios)
        this.unsubscribe = onAuthStateChanged(auth, this.authStateChanged)
        if (!this.$store.state.account.user?.preventLogout) {
            // workaround for e2e testing until we use Firebase emulator
            this.$store.commit('account/logout')
        }

    },
    beforeMount() {
        this.$root.$on('network-error', this.ping)
        this.$root.$on('account-currency-changed', this.accountCurrencyChanged);
    },
    beforeDestroy() {
        clearTimeout(this.timeout)
        this.unsubscribe()
        this.$root.$off('network-error', this.ping)
        this.$root.$off('account-currency-changed', this.accountCurrencyChanged)
    },
    methods: {
        cancelAutoUpdate() {
            clearInterval(this.timer);
        },

        async ping(retry = false) {
            if (this.networkError && !retry) {
                return  // we are already monitoring the connection
            }
            this.networkError = true
            this.overlay = true
            try {
                await AuthService.ping(this.$axios, this.$t)
                this.networkError = false
                this.timeout = setTimeout(() => this.overlay = false, 2000)
            } catch (e) {
                this.timeout = setTimeout(this.ping, PING_INTERVAL_MS, true)
            }
        },
        fetchAccountCurrency() {
            this.portfolioService.getAccount().then(account => {
                this.$store.commit(
                    'account/setSubscribedProductIds',
                    account.subscribedProductIds)
                this.$store.commit(
                    'account/setIsSubscriber', account.isSubscriber)
                this.$store.commit('account/setCurrency', account.currency)
                this.$store.commit('account/setShowNewUsersTour', account.showNewUsersTour)
                this.$store.commit('account/setCustomIcon', account.customIcon)

                this.handleLanguageAfterLogin(account)
            }).catch(e => this.handleBackendException(
                e, this.$t('layouts.default.accountCurrencyFetchError'))
            )
        },
        accountCurrencyChanged(newCurrency) {
            this.$store.commit('account/setCurrency', newCurrency)
        },
        updateErrorMessage() {
            this.errorMessage = '';
        },
        async fetchQuestionUsage() {
            try {
                this.$store.commit('finAgent/setIsLoadingQuestionUsage', true);
                const usage = await this.finAgentService.getUsage();
                this.$store.commit('finAgent/setQuestionUsage', usage);
            } catch (error) {
                if (this.$axios.isCancel(error)) {
                    return;
                }
                this.handleBackendException(error, this.$t('common.errors.generalError'));
            } finally {
                this.$store.commit('finAgent/setIsLoadingQuestionUsage', false);
            }
        }
    },
};
